import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChevronLeft, ChevronRight, FileDown, CalendarDays, Clock, Palmtree, Thermometer, X, CheckCircle2, XCircle, FileText, MapPin, Eye, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MESI = ['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const GIORNI_SETT = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const assenzaConfig = {
  ferie: { label: 'Ferie', short: 'F', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Palmtree },
  malattia: { label: 'Malattia', short: 'M', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Thermometer },
  permesso: { label: 'Permesso', short: 'P', color: 'bg-purple-100 text-purple-700 border-purple-300', icon: Clock },
  festivita: { label: 'Festività', short: 'FE', color: 'bg-red-100 text-red-700 border-red-300', icon: Sparkles },
  ingiustificata: { label: 'Assenza', short: 'A', color: 'bg-red-100 text-red-700 border-red-300', icon: X }
};

export default function PresenzePage() {
  const now = new Date();
  const [anno, setAnno] = useState(now.getFullYear());
  const [mese, setMese] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWorker, setSelectedWorker] = useState('all');
  const [assenzaDialogOpen, setAssenzaDialogOpen] = useState(false);
  const [assenzaForm, setAssenzaForm] = useState({ worker_id: '', data: '', tipo: 'ferie', protocollo: '', note: '' });
  const [richiesteFerie, setRichiesteFerie] = useState([]);
  const [certificatiMalattia, setCertificatiMalattia] = useState([]);
  const [richiestePermesso, setRichiestePermesso] = useState([]);
  // Day detail dialog
  const [dayDetailOpen, setDayDetailOpen] = useState(false);
  const [dayDetailLoading, setDayDetailLoading] = useState(false);
  const [dayDetailData, setDayDetailData] = useState(null);
  const [dayDetailMeta, setDayDetailMeta] = useState({ workerName: '', dateStr: '' });
  // iter52: Festività dialog
  const [festivitaOpen, setFestivitaOpen] = useState(false);
  const [festivitaAnno, setFestivitaAnno] = useState(now.getFullYear());
  const [festivitaList, setFestivitaList] = useState([]);
  const [festivitaApplying, setFestivitaApplying] = useState(false);
  const [festivitaSovrascrivi, setFestivitaSovrascrivi] = useState(false);

  const fetchPresenze = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/presenze?anno=${anno}&mese=${mese}`, { withCredentials: true });
      setData(res.data);
    } catch (error) {
      console.error('Errore caricamento presenze:', error);
      toast.error('Errore nel caricamento');
    }
    finally { setLoading(false); }
  }, [anno, mese]);

  const fetchRichieste = useCallback(async () => {
    try {
      const [ferieRes, certRes, permRes] = await Promise.all([
        axios.get(`${API}/richieste-ferie`, { withCredentials: true }),
        axios.get(`${API}/certificati-malattia`, { withCredentials: true }),
        axios.get(`${API}/richieste-permesso`, { withCredentials: true })
      ]);
      setRichiesteFerie(ferieRes.data);
      setCertificatiMalattia(certRes.data);
      setRichiestePermesso(permRes.data);
    } catch (err) {
      console.error('Errore caricamento richieste:', err);
    }
  }, []);

  useEffect(() => { fetchPresenze(); }, [fetchPresenze]);
  useEffect(() => { fetchRichieste(); }, [fetchRichieste]);

  // Estrai un messaggio di errore leggibile da qualunque tipo di risposta
  // (string detail, array di Pydantic validation errors, errore di rete, etc.)
  const extractErr = (err, fallback) => {
    // Log completo in console per debug rapido
    // eslint-disable-next-line no-console
    console.error('[Approvazione] error', err, err?.response?.data);
    const d = err?.response?.data?.detail;
    if (typeof d === 'string' && d.trim()) return d;
    if (Array.isArray(d) && d.length) {
      // Pydantic validation: [{loc, msg, type}, ...]
      return d.map(e => e?.msg || JSON.stringify(e)).join(' • ');
    }
    if (err?.response?.status) {
      return `${fallback} (HTTP ${err.response.status})`;
    }
    if (err?.message) return `${fallback}: ${err.message}`;
    return fallback;
  };

  const handleApprovaFerie = async (id) => {
    try {
      await axios.put(`${API}/richieste-ferie/${id}/approva`, {}, { withCredentials: true });
      toast.success('Ferie approvate');
      fetchRichieste();
      fetchPresenze();
    } catch (err) { toast.error(extractErr(err, 'Errore nell\'approvazione')); }
  };

  const handleRifiutaFerie = async (id) => {
    try {
      await axios.put(`${API}/richieste-ferie/${id}/rifiuta`, {}, { withCredentials: true });
      toast.success('Ferie rifiutate');
      fetchRichieste();
    } catch (err) { toast.error(extractErr(err, 'Errore nel rifiuto')); }
  };

  const handleApprovaPermesso = async (id) => {
    try {
      await axios.put(`${API}/richieste-permesso/${id}/approva`, {}, { withCredentials: true });
      toast.success('Permesso approvato');
      fetchRichieste();
      fetchPresenze();
    } catch (err) { toast.error(extractErr(err, 'Errore nell\'approvazione')); }
  };

  const handleRifiutaPermesso = async (id) => {
    try {
      await axios.put(`${API}/richieste-permesso/${id}/rifiuta`, {}, { withCredentials: true });
      toast.success('Permesso rifiutato');
      fetchRichieste();
    } catch (err) { toast.error(extractErr(err, 'Errore nel rifiuto')); }
  };

  const prevMonth = () => {
    if (mese === 1) { setMese(12); setAnno(anno - 1); }
    else setMese(mese - 1);
  };
  const nextMonth = () => {
    if (mese === 12) { setMese(1); setAnno(anno + 1); }
    else setMese(mese + 1);
  };

  const openAssenzaDialog = (workerId, dateStr) => {
    setAssenzaForm({ worker_id: workerId, data: dateStr, tipo: 'ferie', protocollo: '', note: '' });
    setAssenzaDialogOpen(true);
  };

  const openDayDetail = async (worker, dateStr) => {
    setDayDetailMeta({ workerName: worker.worker_name, dateStr });
    setDayDetailOpen(true);
    setDayDetailLoading(true);
    setDayDetailData(null);
    try {
      const res = await axios.get(`${API}/presenze/day-detail`, {
        params: { worker_id: worker.worker_id, data: dateStr },
        withCredentials: true,
      });
      setDayDetailData(res.data || []);
    } catch {
      toast.error('Errore caricamento dettaglio giorno');
      setDayDetailData([]);
    } finally {
      setDayDetailLoading(false);
    }
  };

  const handleDayClick = (worker, dateStr, hasWork, assenza) => {
    if (hasWork) {
      openDayDetail(worker, dateStr);
    } else if (!assenza) {
      openAssenzaDialog(worker.worker_id, dateStr);
    }
  };

  const handleAddAssenza = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/assenze`, assenzaForm, { withCredentials: true });
      toast.success('Assenza registrata');
      setAssenzaDialogOpen(false);
      fetchPresenze();
    } catch (error) { toast.error('Errore'); }
  };

  const handleDeleteAssenza = async (workerId, dateStr) => {
    try {
      await axios.delete(`${API}/assenze?worker_id=${workerId}&data=${dateStr}`, { withCredentials: true });
      toast.success('Assenza rimossa');
      fetchPresenze();
    } catch (error) { toast.error('Errore'); }
  };

  const handleDownloadPdf = async () => {
    try {
      const params = `anno=${anno}&mese=${mese}${selectedWorker !== 'all' ? `&worker_id=${selectedWorker}` : ''}`;
      const response = await axios.get(`${API}/presenze/report-pdf?${params}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `presenze_${MESI[mese]}_${anno}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF scaricato');
    } catch { toast.error('Errore nel download'); }
  };

  const getWeekday = (day) => {
    const d = new Date(anno, mese - 1, day);
    return d.getDay();
  };
  const isWeekend = (day) => { const wd = getWeekday(day); return wd === 0 || wd === 6; };

  const filteredWorkers = data?.workers?.filter(w => selectedWorker === 'all' || w.worker_id === selectedWorker) || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Robusto a vari formati ricevuti dal backend (ISO con timestamp,
    // YYYY-MM-DD, YYYY/MM/DD): estraiamo i primi 10 caratteri normalizzando
    // gli slash a trattini per evitare di mostrare 'undefined/undefined/...'
    const head = String(dateStr).slice(0, 10).replace(/\//g, '-');
    const parts = head.split('-');
    if (parts.length !== 3 || parts.some(p => !p)) return String(dateStr);
    const [y, m, d] = parts;
    return `${d}-${m}-${y}`;
  };

  const pendingFerie = richiesteFerie.filter(r => r.stato === 'in_attesa');
  const pendingPermessi = richiestePermesso.filter(r => r.stato === 'in_attesa');
  const totalPending = pendingFerie.length + pendingPermessi.length;
  const recentCertificati = certificatiMalattia.slice(0, 10);

  // Pure helpers (extracted to avoid nested ternaries in JSX)
  const defaultRichiesteTab = (numFerie, numPermessi) => {
    if (numFerie > 0) return 'richieste';
    if (numPermessi > 0) return 'permessi';
    return 'certificati';
  };
  const rapportinoStatusClass = (status) => {
    if (status === 'approvato') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (status === 'rifiutato') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-amber-50 text-amber-700 border-amber-200';
  };

  // iter52: Festività handlers
  const openFestivitaDialog = async () => {
    setFestivitaOpen(true);
    try {
      const res = await axios.get(`${API}/festivita?anno=${festivitaAnno}`, { withCredentials: true });
      setFestivitaList(res.data?.festivita || []);
    } catch {
      toast.error('Errore nel caricamento festività');
    }
  };

  const reloadFestivitaList = useCallback(async (year) => {
    try {
      const res = await axios.get(`${API}/festivita?anno=${year}`, { withCredentials: true });
      setFestivitaList(res.data?.festivita || []);
    } catch {
      setFestivitaList([]);
    }
  }, []);

  useEffect(() => {
    if (festivitaOpen) reloadFestivitaList(festivitaAnno);
  }, [festivitaAnno, festivitaOpen, reloadFestivitaList]);

  const applyFestivita = async () => {
    if (!window.confirm(`Applicare le 12 festività nazionali ${festivitaAnno} a tutti gli operai?`)) return;
    setFestivitaApplying(true);
    try {
      const res = await axios.post(
        `${API}/festivita/applica`,
        { anno: festivitaAnno, sovrascrivi: festivitaSovrascrivi },
        { withCredentials: true }
      );
      const { inserite, aggiornate, saltate, operai_processati } = res.data;
      toast.success(`Festività ${festivitaAnno} applicate: ${inserite} nuove, ${aggiornate} aggiornate, ${saltate} saltate · ${operai_processati} operai`);
      setFestivitaOpen(false);
      await fetchPresenze();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore applicazione festività');
    } finally {
      setFestivitaApplying(false);
    }
  };

  const removeFestivita = async () => {
    if (!window.confirm(`Rimuovere TUTTE le festività ${festivitaAnno}? (operazione irreversibile)`)) return;
    setFestivitaApplying(true);
    try {
      const res = await axios.delete(`${API}/festivita/rimuovi?anno=${festivitaAnno}`, { withCredentials: true });
      toast.success(`Rimosse ${res.data?.rimosse || 0} assenze festività ${festivitaAnno}`);
      setFestivitaOpen(false);
      await fetchPresenze();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore rimozione festività');
    } finally {
      setFestivitaApplying(false);
    }
  };

  if (loading && !data) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="presenze-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Presenze</h1>
          <p className="text-slate-500 mt-1">Calendario presenze, ferie e malattie</p>
        </div>
        <div className="flex items-center gap-3">
          {totalPending > 0 && (
            <Badge className="bg-amber-500 text-white hover:bg-amber-600" data-testid="pending-ferie-badge">
              {totalPending} richieste in attesa
            </Badge>
          )}
          <Button variant="outline" onClick={handleDownloadPdf} data-testid="download-presenze-pdf">
            <FileDown className="h-4 w-4 mr-2" />PDF
          </Button>
          <Button variant="outline" onClick={openFestivitaDialog} className="border-red-200 text-red-700 hover:bg-red-50" data-testid="open-festivita-dialog">
            <Sparkles className="h-4 w-4 mr-2" />Festività
          </Button>
          <Select value={selectedWorker} onValueChange={setSelectedWorker}>
            <SelectTrigger className="w-48" data-testid="filter-worker">
              <SelectValue placeholder="Tutti gli operai" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli operai</SelectItem>
              {data?.workers?.map(w => <SelectItem key={w.worker_id} value={w.worker_id}>{w.worker_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Month Navigator */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="prev-month"><ChevronLeft className="h-5 w-5" /></Button>
        <h2 className="text-xl font-bold text-slate-900 min-w-[200px] text-center" data-testid="current-month">{MESI[mese]} {anno}</h2>
        <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="next-month"><ChevronRight className="h-5 w-5" /></Button>
      </div>

      {/* Workers Calendar */}
      {filteredWorkers.map((worker) => (
        <Card key={worker.worker_id} className="border-slate-200" data-testid={`worker-calendar-${worker.worker_id}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center">
                  <span className="text-sm font-medium text-slate-600">{worker.worker_name?.charAt(0)}</span>
                </div>
                {worker.worker_name}
              </CardTitle>
              <div className="flex items-center gap-3 text-sm flex-wrap">
                <span className="text-slate-600"><strong>{worker.totale_ore}</strong>h totali</span>
                {worker.totale_straordinari > 0 && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Straord: {worker.totale_straordinari}h</Badge>}
                {worker.giorni_trasferta > 0 && <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">Trasf: {worker.giorni_trasferta}gg</Badge>}
                <span className="text-slate-600"><strong>{worker.giorni_lavorati}</strong> gg lavorati</span>
                {worker.giorni_ferie > 0 && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">F: {worker.giorni_ferie}</Badge>}
                {worker.giorni_malattia > 0 && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">M: {worker.giorni_malattia}</Badge>}
                {worker.giorni_permesso > 0 && <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">P: {worker.giorni_permesso}</Badge>}
                {worker.giorni_festivita > 0 && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">FE: {worker.giorni_festivita}</Badge>}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="grid grid-cols-7 gap-1">
              {/* Day of week headers */}
              {GIORNI_SETT.map(g => (
                <div key={g} className="text-center text-[10px] font-medium text-slate-400 uppercase py-1">{g}</div>
              ))}
              {/* Empty cells for offset */}
              {Array.from({ length: (getWeekday(1) + 6) % 7 }).map((_, i) => (
                <div key={`empty-${i}`} />
              ))}
              {/* Day cells */}
              {Array.from({ length: data?.days_in_month || 30 }, (_, i) => i + 1).map(day => {
                const dateStr = `${anno}-${mese.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
                const info = worker.giorni[dateStr] || {};
                const weekend = isWeekend(day);
                const hasWork = info.ore > 0;
                const assenza = info.assenza;
                const cfg = assenza ? assenzaConfig[assenza] : null;

                return (
                  <div
                    key={day}
                    className={`relative min-h-[52px] rounded-md border p-1 cursor-pointer transition-all hover:shadow-sm
                      ${weekend ? 'bg-slate-50 border-slate-200' : 'border-slate-200'}
                      ${hasWork ? 'bg-emerald-50 border-emerald-200' : ''}
                      ${assenza ? cfg.color : ''}
                    `}
                    onClick={() => handleDayClick(worker, dateStr, hasWork, assenza)}
                    data-testid={`day-${worker.worker_id}-${day}`}
                  >
                    <div className="flex items-start justify-between">
                      <span className={`text-xs font-medium ${weekend ? 'text-slate-400' : 'text-slate-700'}`}>{day}</span>
                      {assenza && (
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteAssenza(worker.worker_id, dateStr); }} className="text-slate-400 hover:text-red-500 p-0.5">
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    {hasWork && (
                      <div className="mt-0.5">
                        <p className="text-[10px] font-bold text-emerald-700">{info.ore}h</p>
                        {info.ore_straordinarie > 0 && <p className="text-[8px] font-bold text-red-600">+{info.ore_straordinarie}h str</p>}
                        {info.trasferta && <p className="text-[8px] font-bold text-orange-600">T</p>}
                        {info.orario && <p className="text-[8px] text-emerald-600">{info.orario}</p>}
                      </div>
                    )}
                    {assenza && (
                      <div className="mt-0.5">
                        <p className="text-[10px] font-bold">{cfg.label}</p>
                        {info.protocollo && <p className="text-[8px]">Prot: {info.protocollo}</p>}
                      </div>
                    )}
                    {!hasWork && !assenza && !weekend && (
                      <p className="text-[9px] text-slate-300 mt-1">+</p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Legenda */}
      <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
        <span className="font-medium">Legenda:</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300"></span> Lavorato</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-100 border border-blue-300"></span> Ferie (F)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300"></span> Malattia (M)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-100 border border-purple-300"></span> Permesso (P)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-50 border border-slate-200"></span> Weekend</span>
        <span className="flex items-center gap-1 text-red-600 font-medium">+Xh str = Straordinari</span>
        <span className="flex items-center gap-1 text-orange-600 font-medium">T = Trasferta</span>
      </div>

      {/* RICHIESTE FERIE, PERMESSI & CERTIFICATI */}
      <Tabs defaultValue={defaultRichiesteTab(pendingFerie.length, pendingPermessi.length)} className="w-full">
        <TabsList className="w-full grid grid-cols-3">
          <TabsTrigger value="richieste" className="flex items-center gap-1.5" data-testid="tab-richieste-ferie">
            <Palmtree className="h-4 w-4" />
            Ferie {pendingFerie.length > 0 && <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0 ml-1">{pendingFerie.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="permessi" className="flex items-center gap-1.5" data-testid="tab-richieste-permesso">
            <Clock className="h-4 w-4" />
            Permessi {pendingPermessi.length > 0 && <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0 ml-1">{pendingPermessi.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="certificati" className="flex items-center gap-1.5" data-testid="tab-certificati-malattia">
            <Thermometer className="h-4 w-4" />Certificati
          </TabsTrigger>
        </TabsList>

        <TabsContent value="richieste" className="space-y-3 mt-4">
          {richiesteFerie.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Nessuna richiesta ferie</p>
          ) : (
            richiesteFerie.map(r => (
              <Card key={r.id} className={`border-slate-200 ${r.stato === 'in_attesa' ? 'border-l-4 border-l-amber-400' : ''}`} data-testid={`admin-richiesta-ferie-${r.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-xs font-bold text-blue-700">{r.worker_name?.charAt(0)}</span>
                        </div>
                        <span className="font-medium text-sm">{r.worker_name}</span>
                      </div>
                      <p className="text-sm text-slate-700">
                        {formatDate(r.data_inizio)} - {formatDate(r.data_fine)}
                      </p>
                      {r.note && <p className="text-xs text-slate-500">{r.note}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {r.stato === 'in_attesa' ? (
                        <>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" onClick={() => handleApprovaFerie(r.id)} data-testid={`approva-ferie-${r.id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approva
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-8" onClick={() => handleRifiutaFerie(r.id)} data-testid={`rifiuta-ferie-${r.id}`}>
                            <XCircle className="h-3.5 w-3.5 mr-1" />Rifiuta
                          </Button>
                        </>
                      ) : (
                        <Badge variant="outline" className={r.stato === 'approvata' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}>
                          {r.stato === 'approvata' ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                          {r.stato === 'approvata' ? 'Approvata' : 'Rifiutata'}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="permessi" className="space-y-3 mt-4">
          {richiestePermesso.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Nessuna richiesta permesso</p>
          ) : (
            richiestePermesso.map(r => (
              <Card key={r.id} className={`border-slate-200 ${r.stato === 'in_attesa' ? 'border-l-4 border-l-purple-400' : ''}`} data-testid={`admin-richiesta-permesso-${r.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-purple-100 flex items-center justify-center">
                          <span className="text-xs font-bold text-purple-700">{r.worker_name?.charAt(0)}</span>
                        </div>
                        <span className="font-medium text-sm">{r.worker_name}</span>
                      </div>
                      <p className="text-sm text-slate-700">
                        {formatDate(r.data_inizio)} - {formatDate(r.data_fine)}
                      </p>
                      {r.ore_permesso > 0 && <p className="text-xs text-purple-600">{r.ore_permesso}h di permesso</p>}
                      {r.note && <p className="text-xs text-slate-500">{r.note}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {r.stato === 'in_attesa' ? (
                        <>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" onClick={() => handleApprovaPermesso(r.id)} data-testid={`approva-permesso-${r.id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approva
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-8" onClick={() => handleRifiutaPermesso(r.id)} data-testid={`rifiuta-permesso-${r.id}`}>
                            <XCircle className="h-3.5 w-3.5 mr-1" />Rifiuta
                          </Button>
                        </>
                      ) : (
                        <Badge variant="outline" className={r.stato === 'approvata' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}>
                          {r.stato === 'approvata' ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                          {r.stato === 'approvata' ? 'Approvato' : 'Rifiutato'}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="certificati" className="space-y-3 mt-4">
          {certificatiMalattia.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Nessun certificato ricevuto</p>
          ) : (
            certificatiMalattia.map(c => (
              <Card key={c.id} className="border-slate-200" data-testid={`admin-certificato-${c.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-amber-100 flex items-center justify-center">
                          <span className="text-xs font-bold text-amber-700">{c.worker_name?.charAt(0)}</span>
                        </div>
                        <span className="font-medium text-sm">{c.worker_name}</span>
                      </div>
                      <p className="text-sm text-slate-700">
                        {formatDate(c.data_inizio)} - {formatDate(c.data_fine)}
                      </p>
                      <p className="text-xs text-slate-600">Protocollo INPS: <strong>{c.protocollo_inps}</strong></p>
                      {c.note && <p className="text-xs text-slate-500">{c.note}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {c.file_url && (
                        <a href={c.file_url} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="h-8" data-testid={`view-cert-${c.id}`}>
                            <FileText className="h-3.5 w-3.5 mr-1" />Vedi
                          </Button>
                        </a>
                      )}
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        <Thermometer className="h-3 w-3 mr-1" />{c.stato || 'Ricevuto'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Day Detail Dialog */}
      <Dialog open={dayDetailOpen} onOpenChange={setDayDetailOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-emerald-600" />
              Cantieri del {formatDate(dayDetailMeta.dateStr)}
            </DialogTitle>
            <p className="text-sm text-slate-500">{dayDetailMeta.workerName}</p>
          </DialogHeader>
          <div className="mt-2">
            {(() => {
              if (dayDetailLoading) {
                return <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900" /></div>;
              }
              if (!dayDetailData || dayDetailData.length === 0) {
                return <p className="text-sm text-slate-400 text-center py-6">Nessun rapportino trovato per questo giorno</p>;
              }
              return (
              <div className="space-y-3" data-testid="day-detail-list">
                {dayDetailData.map((r, idx) => (
                  <Card key={r.rapportino_id || idx} className="border-slate-200" data-testid={`day-detail-item-${idx}`}>
                    <CardContent className="p-3 sm:p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-emerald-600 shrink-0" />
                            <p className="font-semibold text-slate-900 truncate">{r.cantiere_name}</p>
                            {r.numero ? <Badge variant="outline" className="text-[10px] shrink-0">#{r.numero}</Badge> : null}
                          </div>
                          {r.cantiere_address && (
                            <p className="text-xs text-slate-500 mt-0.5 ml-6">{r.cantiere_address}</p>
                          )}
                        </div>
                        {r.status && (
                          <Badge variant="outline" className={`text-[10px] shrink-0 ${rapportinoStatusClass(r.status)}`}>{r.status}</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        {(r.ora_inizio || r.ora_fine) && (
                          <span className="inline-flex items-center gap-1 text-slate-700 font-medium">
                            <Clock className="h-3.5 w-3.5" />
                            {r.ora_inizio || '?'} - {r.ora_fine || '?'}
                          </span>
                        )}
                        {r.hours_worked > 0 && (
                          <span className="text-slate-600">{r.hours_worked}h totali</span>
                        )}
                        {r.ore_straordinarie > 0 && (
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px]">+{r.ore_straordinarie}h straord.</Badge>
                        )}
                        {r.trasferta && (
                          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-[10px]">Trasferta</Badge>
                        )}
                      </div>
                      {r.description && (
                        <p className="text-xs text-slate-600 bg-slate-50 rounded p-2 line-clamp-3">{r.description}</p>
                      )}
                      {r.all_workers && r.all_workers.length > 1 && (
                        <p className="text-[11px] text-slate-500"><strong>Squadra:</strong> {r.all_workers.join(', ')}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              );
            })()}
          </div>
          <div className="flex justify-end pt-3 border-t border-slate-100">
            <Button variant="outline" onClick={() => setDayDetailOpen(false)} data-testid="close-day-detail">Chiudi</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assenza Dialog */}
      <Dialog open={assenzaDialogOpen} onOpenChange={setAssenzaDialogOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Registra Assenza</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddAssenza} className="space-y-4 mt-4">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-500">Data: <strong>{assenzaForm.data}</strong></p>
              <p className="text-sm text-slate-500">Operaio: <strong>{data?.workers?.find(w => w.worker_id === assenzaForm.worker_id)?.worker_name}</strong></p>
            </div>
            <div className="space-y-2">
              <Label>Tipo Assenza</Label>
              <Select value={assenzaForm.tipo} onValueChange={(v) => setAssenzaForm({...assenzaForm, tipo: v})}>
                <SelectTrigger data-testid="assenza-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ferie">Ferie</SelectItem>
                  <SelectItem value="malattia">Malattia</SelectItem>
                  <SelectItem value="permesso">Permesso</SelectItem>
                  <SelectItem value="festivita">Festività</SelectItem>
                  <SelectItem value="ingiustificata">Assenza Ingiustificata</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {assenzaForm.tipo === 'malattia' && (
              <div className="space-y-2">
                <Label>Numero Protocollo</Label>
                <Input value={assenzaForm.protocollo} onChange={(e) => setAssenzaForm({...assenzaForm, protocollo: e.target.value})} placeholder="Protocollo INPS" data-testid="assenza-protocollo-input" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Input value={assenzaForm.note} onChange={(e) => setAssenzaForm({...assenzaForm, note: e.target.value})} placeholder="Note aggiuntive" data-testid="assenza-note-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAssenzaDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-assenza-btn">Salva</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* iter52: Dialog Festività nazionali */}
      <Dialog open={festivitaOpen} onOpenChange={setFestivitaOpen}>
        <DialogContent className="max-w-lg" data-testid="festivita-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-red-600" />Festività nazionali italiane</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Label className="text-sm">Anno</Label>
              <Input
                type="number"
                min="1900"
                max="2200"
                value={festivitaAnno}
                onChange={(e) => setFestivitaAnno(parseInt(e.target.value, 10) || now.getFullYear())}
                className="w-28"
                data-testid="festivita-anno-input"
              />
              <span className="text-xs text-slate-500">{festivitaList.length} festività</span>
            </div>
            <div className="border rounded-md max-h-64 overflow-y-auto" data-testid="festivita-list">
              {festivitaList.length === 0 ? (
                <p className="text-sm text-slate-500 p-4 text-center">Caricamento…</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {festivitaList.map((f) => (
                    <li key={f.data} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="font-mono text-xs text-slate-600">{f.data}</span>
                      <span className="text-slate-900 flex-1 ml-3">{f.nome}</span>
                      <Badge variant="outline" className={f.tipo === 'mobile' ? 'bg-purple-50 text-purple-700 border-purple-200 text-[10px]' : 'bg-red-50 text-red-700 border-red-200 text-[10px]'}>
                        {f.tipo}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={festivitaSovrascrivi}
                onChange={(e) => setFestivitaSovrascrivi(e.target.checked)}
                data-testid="festivita-sovrascrivi"
              />
              Sovrascrivi assenze esistenti (ferie/permessi nei giorni festivi → vengono sostituite)
            </label>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
              <strong>Cosa fa "Applica":</strong> crea automaticamente un'assenza tipo "Festività" per ogni operaio in ognuna delle date sopra. Le assenze già presenti vengono saltate (a meno che la checkbox sopra sia attiva).
            </div>
            <div className="flex justify-between gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={removeFestivita}
                disabled={festivitaApplying}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="rimuovi-festivita-btn"
              >
                Rimuovi tutte
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setFestivitaOpen(false)} disabled={festivitaApplying}>Chiudi</Button>
                <Button
                  type="button"
                  className="bg-red-600 text-white hover:bg-red-700"
                  onClick={applyFestivita}
                  disabled={festivitaApplying || festivitaList.length === 0}
                  data-testid="applica-festivita-btn"
                >
                  {festivitaApplying ? 'Applico…' : `Applica ${festivitaAnno}`}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
